// Utils and Service
import { getErrorMessage, isCanceledError } from '../utils/helpers'

import axios from './instance'

/**
 * Gets the Dealer for the specified `id`.
 * @param {string} dealerId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const getDealer = async (
  dealerId,
  setLoading,
  setError,
  setSuccess = () => {},
  expand = 'group,manufacturer',
  signal = undefined,
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${dealerId}/?expand=${expand}`, { signal })

    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    const error = getErrorMessage(err)

    // Session expiry was already surfaced by the response interceptor;
    // getErrorMessage returns null specifically in that case. Bail without a
    // duplicate toast and without crashing on the null .includes checks below.
    // Unlike a canceled request, no newer request owns the spinner here, so
    // clear loading before returning.
    if (error === null) {
      setLoading(false)
      return null
    }

    if (
      error.includes('given query') ||
      error.includes('%(value)s') ||
      error.includes('Not found')
    ) {
      setError('Dealer not found.')
    } else {
      setError(error)
    }
    setLoading(false)
    return null
  }
}

/**
 * Updates the dealer with the specified `payload`.
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const updateDealer = async (payload, setLoading, setError, setSuccess = () => {}) => {
  setLoading(true)

  try {
    const { data } = await axios.patch(`/dealers/${payload.id}/?expand=group`, payload)

    setSuccess(true)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the repair orders using the specified `url`.
 * @param {string} url
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 * @param {AbortSignal} signal - cancels the request when a newer one supersedes it
 */
export const getRepairOrders = async (
  url,
  setLoading,
  setError,
  setSuccess = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const response = await axios.get(url, { signal })

    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the KPIs using the specified `url`.
 * @param {string} url
 * @param {func} setLoading
 * @param {func} setError
 * @returns `data` results
 */
export const getRepairOrderKpis = async (
  url,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(url, { signal })

    setLoading(false)
    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the actions needed for the specified `dealerId`.
 * @param {string} dealerId
 * @param {boolean} resolved
 * @param {func} setLoading
 * @param {func} setError
 * @returns `data` results
 */
export const getActionsNeeded = async (
  dealerId,
  resolved,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/dealers/${dealerId}/repair-orders/actions/?resolved=${resolved}`,
      { signal },
    )

    setLoading(false)
    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the advisors needed for the specified `dealerId`.
 * @param {string} dealerId
 * @param {boolean} resolved
 * @param {func} setLoading
 * @param {func} setError
 * @returns `data` results
 */
export const getAdvisors = async (
  dealerId,
  resolved,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/dealers/${dealerId}/repair-orders/advisors/?resolved=${resolved}`,
      { signal },
    )

    setLoading(false)
    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the technicians needed for the specified `dealerId`.
 * @param {string} dealerId
 * @param {boolean} resolved
 * @param {func} setLoading
 * @param {func} setError
 * @returns `data` results
 */
export const getTechnicians = async (
  dealerId,
  resolved,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const data = await axios.get(
      `/dealers/${dealerId}/repair-orders/technicians/?resolved=${resolved}`,
      { signal },
    )

    setLoading(false)
    return data
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/*
 * Creates a repair order export with the specified `url`.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 */
export const createRepairOrderExport = async (
  url,
  setError = () => {},
  setLoading = () => {},
  payload = {},
) => {
  setLoading(true)

  try {
    const response = await axios.post(url, payload)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/*
 * Gets the repair order export with the specified `dealerId` and `exportId`.
 * @param {string} dealerId
 * @param {string} exportId
 * @param {func} setError
 * @param {func} setLoading
 */
export const getRepairOrderExport = async (
  dealerId,
  exportId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${dealerId}/repair-order-exports/${exportId}`)

    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/*
 * Fetches dealer metrics from the generic metrics endpoint.
 * @param {string} dealerId
 * @param {object} params - Query params: metric, start, end, groupBy
 * @param {func} setLoading
 * @param {func} setError
 */
export const getDealerMetrics = async (
  dealerId,
  params,
  setLoading = () => {},
  setError = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${dealerId}/metrics/`, { params })
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
